import {
  DirectedTripleWithFilters,
  PathResult,
  APIReferenceAttributes,
  ComponentPath,
  ReferenceDirection,
  ArdoqId,
} from '@ardoq/api-types';
import { toByIdDictionary } from '@ardoq/common-helpers';

export const toComponentPaths = (
  paths: DirectedTripleWithFilters[][],
  allPathResults: PathResult[],
  references: APIReferenceAttributes[]
): ComponentPath[][] => {
  const referencesById = toByIdDictionary(references);
  const result: ComponentPath[][] = paths.map(() => []);
  allPathResults.forEach(({ pathIndex, pathResults, startComponentId }) => {
    const compPaths = pathResults.map(referencePath => {
      return [
        {
          componentId: startComponentId,
          direction: ReferenceDirection.UNSET,
          referenceId: '',
          isParentChildReference: false,
        },
        ...toSingleComponentPath(
          referencePath,
          startComponentId,
          referencesById
        ),
      ];
    });
    result[pathIndex] = [...(result[pathIndex] ?? []), ...compPaths];
  });
  return result;
};

const toSingleComponentPath = (
  referencePath: ArdoqId[],
  startComponentId: ArdoqId,
  referencesById: Record<ArdoqId, APIReferenceAttributes>
) =>
  referencePath.reduce<{
    currentTail: string;
    componentPath: ComponentPath;
  }>(
    ({ currentTail, componentPath }, referenceId) => {
      const componentPathEntry = referenceIdToComponentPathEntry(
        referencesById,
        referenceId,
        currentTail
      );
      componentPath.push(componentPathEntry);
      return {
        currentTail: componentPathEntry.componentId,
        componentPath,
      };
    },
    { currentTail: startComponentId, componentPath: [] }
  ).componentPath;

const referenceIdToComponentPathEntry = (
  referencesById: Record<ArdoqId, APIReferenceAttributes>,
  referenceId: string,
  currentTail: string
): ComponentPath[0] => {
  const isParentChildReference = referenceId.includes(':');
  const reference = referencesById[referenceId];
  const { source, target } = reference;
  const nextTail = source === currentTail ? target : source;
  const direction = getDirection(source, currentTail, isParentChildReference);

  return {
    componentId: nextTail,
    referenceId: toFEReferenceId(referenceId),
    direction,
    isParentChildReference,
  };
};

const toFEReferenceId = (referenceId: string) => {
  if (!referenceId.includes(':')) {
    return referenceId;
  }
  const [childId, parentId] = referenceId.split(':');
  return `${parentId}-${childId}`;
};

const getDirection = (
  source: string,
  currentTail: string,
  isParentChildReference: boolean
) => {
  if (isParentChildReference) {
    // We are working with BE parent-child references here. On the BE a child
    // is set as source, the parent set as target. For the FE we flip this
    // around, so that the parent is the source and the child is the target.
    return source === currentTail
      ? ReferenceDirection.INCOMING
      : ReferenceDirection.OUTGOING;
  }

  return source === currentTail
    ? ReferenceDirection.OUTGOING
    : ReferenceDirection.INCOMING;
};
